/**
 * Módulo de Mensageria e Mídias Uazapi
 * Arquitetura: Funções de envio e formatação em um módulo isolado.
 * Lida com formatação e requisições para envio de texto, mídias e reações.
 */

import * as uazapi from './index';
import { editMessage as outboundEditMessage, deleteMessage as outboundDeleteMessage, type PixKeyType } from './outbound';
import { fetchMessages as fetchSyncMessages } from './sync';
/**
 * Normaliza resposta de envio para formato esperado pelo frontend { key: { id } }
 */
const formatSentMessageResponse = (res: any) => {
    const messageId =
        res?.messageid ||
        res?.message_id ||
        res?.messageId ||
        res?.id ||
        res?.data?.messageid ||
        res?.data?.id ||
        res?.Info?.ID ||
        res?.key?.id ||
        `sent-${Date.now()}`;
    return {
        ...res,
        key: { id: messageId },
        messageId
    };
};

export const sendTextMessage = async (instanceName: string, number: string, text: string, quotedMessage?: any, requestId?: string, onAccepted?: () => void) => {
    const res = await uazapi.sendText(instanceName, number, text, {
        quotedMessageId: quotedMessage?.id,
        quotedPayload: quotedMessage,
        requestId, onAccepted,
    });
    return formatSentMessageResponse(res);
};

export const sendImage = async (instanceName: string, number: string, imageUrl: string, caption?: string, mimeType?: string, requestId?: string, onAccepted?: () => void) => {
    const res = await uazapi.sendMedia(instanceName, number, imageUrl, mimeType || 'image/jpeg', caption, { requestId, onAccepted });
    return formatSentMessageResponse(res);
};

export const sendVideo = async (instanceName: string, number: string, videoUrl: string, caption?: string, mimeType?: string, requestId?: string, onAccepted?: () => void) => {
    const res = await uazapi.sendMedia(instanceName, number, videoUrl, mimeType || 'video/mp4', caption, { requestId, onAccepted });
    return formatSentMessageResponse(res);
};

export const sendAudio = async (instanceName: string, number: string, audioUrl: string, mimeType?: string, requestId?: string, onAccepted?: () => void) => {
    // Uazapi transcodifica mp3/ogg/wav para OPUS. Rotular webm/mp4 como ogg corta o áudio no PTT.
    const normalized = (mimeType || '').split(';')[0].trim().toLowerCase();
    const mimetype = normalized.startsWith('audio/') ? normalized : 'audio/ogg';
    const res = await uazapi.sendMedia(instanceName, number, audioUrl, mimetype, undefined, { requestId, onAccepted });
    return formatSentMessageResponse(res);
};

export const sendDocument = async (
    instanceName: string,
    number: string,
    documentUrl: string,
    filename?: string,
    mimeType?: string,
    caption?: string,
    requestId?: string,
    onAccepted?: () => void,
) => {
    const res = await uazapi.sendMedia(
        instanceName,
        number,
        documentUrl,
        mimeType || 'application/octet-stream',
        caption,
        { docName: filename, requestId, onAccepted }
    );
    return formatSentMessageResponse(res);
};

export const sendContactMessage = async (
    instanceName: string,
    number: string,
    contact: { fullName: string; phoneNumber: string; organization?: string; email?: string },
    requestId?: string,
    onAccepted?: () => void,
) => {
    const res = await uazapi.sendContact(instanceName, number, contact, { requestId, onAccepted });
    return formatSentMessageResponse(res);
};

export const sendLocationMessage = async (
    instanceName: string,
    number: string,
    location: { latitude: number; longitude: number; name?: string; address?: string },
    requestId?: string,
    onAccepted?: () => void,
) => {
    const res = await uazapi.sendLocation(instanceName, number, location, { requestId, onAccepted });
    return formatSentMessageResponse(res);
};

export const sendPixButtonMessage = async (
    instanceName: string,
    number: string,
    pix: { pixKey: string; type: PixKeyType; name?: string },
    requestId?: string,
    onAccepted?: () => void,
) => {
    const res = await uazapi.sendPixButton(instanceName, number, pix, { requestId, onAccepted });
    return formatSentMessageResponse(res);
};

export const sendSticker = async (instanceName: string, number: string, stickerUrl: string) => {
    const res = await uazapi.sendMedia(instanceName, number, stickerUrl, 'image/webp', undefined, { mediaType: 'sticker' });
    return formatSentMessageResponse(res);
};

export const sendReaction = async (instanceName: string, number: string, messageId: string, emoji: string) => {
    return uazapi.sendReaction(instanceName, number, messageId, emoji);
};

export const editMessage = async (instanceName: string, number: string, messageId: string, newText: string) => {
    return outboundEditMessage(instanceName, number, messageId, newText);
};

export const deleteMessageFromChat = async (instanceName: string, number: string, messageId: string) => {
    return outboundDeleteMessage(instanceName, number, messageId);
};

export const fetchMessages = async (instanceName: string, remoteJid: string, limit = 100) => {
    const messages = await fetchSyncMessages(instanceName, remoteJid, { limit });
    
    if (!messages || !Array.isArray(messages)) return [];
    
    return messages.map((msg: any) => {
        let textValue = msg.text || msg.content?.text || '';
        // Tratar ExtendedTextMessage e formatos especiais de texto
        if (!textValue && msg.messageType === 'ExtendedTextMessage') {
             textValue = msg.content?.text || msg.content?.extendedTextMessage?.text || '';
        }
        
        return {
            key: {
                id: msg.messageid,
                remoteJid: remoteJid,
                fromMe: msg.fromMe || false
            },
            messageTimestamp: msg.messageTimestamp,
            message: { text: textValue },
            messageType: msg.messageType,
            fileURL: msg.fileURL,
            status: msg.status,
            pushName: msg.senderName
        };
    });
};
